"use client";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button } from "react-bootstrap";
import { BASEAPIURL, token } from "../../env";

const FacilityGroupManager = () => {
  const [facilities, setFacilities] = useState([]);
  const [facilityGroups, setFacilityGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDesFiles, setSelectedDesFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [previewDesUrls, setPreviewDesUrls] = useState([]);
  const fileInputRef = useRef(null);
  const desFileInputRef = useRef(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const itemsPerPage = 10;

  const [form, setForm] = useState({
    Facility_ID: "",
    Facility_Name: "",
    Facility_EC_Name: "",
    Facility_TypeID: "",
    Facility_Image: [],
    Facility_DesImage: [],
    Facility_Description: "",
    // StartDate: '',
    // EndDate: '',
    IsActive: false,
    IsBooking: false,
    Facility_EC_GrpID: "", // Added for upload API only
  });

  // Clean up preview URLs
  const revokeUrls = useCallback((urls) => {
    urls.forEach((url) => {
      if (url && url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    });
  }, []);

  useEffect(() => {
    const newUrls = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(newUrls);
    return () => revokeUrls(newUrls);
  }, [selectedFiles, revokeUrls]);

  useEffect(() => {
    const newUrls = selectedDesFiles.map((file) => URL.createObjectURL(file));
    setPreviewDesUrls(newUrls);
    return () => revokeUrls(newUrls);
  }, [selectedDesFiles, revokeUrls]);

  // Reset form
  const resetForm = useCallback(() => {
    setForm({
      Facility_ID: "",
      Facility_Name: "",
      Facility_EC_Name: "",
      Facility_TypeID: "",
      Facility_Image: [],
      Facility_DesImage: [],
      Facility_Description: "",
      // StartDate: '',
      // EndDate: '',
      IsActive: false,
      IsBooking: false,
      Facility_EC_GrpID: "", // Reset EC ID
    });
    setSelectedFiles([]);
    setSelectedDesFiles([]);
    setPreviewUrls([]);
    setPreviewDesUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (desFileInputRef.current) desFileInputRef.current.value = "";
    setIsEdit(false);
  }, []);

  // Image compression function
  // const compressImage = useCallback((file, quality = 0.6) => {
  //   return new Promise((resolve) => {
  //     const canvas = document.createElement('canvas');
  //     const ctx = canvas.getContext('2d');
  //     const img = new Image();

  //     img.onload = () => {
  //       // Set canvas dimensions to match image
  //       canvas.width = img.width;
  //       canvas.height = img.height;

  //       // Draw image on canvas
  //       ctx.drawImage(img, 0, 0);

  //       // Convert to blob with compression
  //       canvas.toBlob(
  //         (blob) => {
  //           if (blob) {
  //             // Create new file with compressed data
  //             const compressedFile = new File([blob], file.name, {
  //               type: file.type,
  //               lastModified: Date.now()
  //             });
  //             resolve(compressedFile);
  //           } else {
  //             resolve(file); // Return original if compression fails
  //           }
  //         },
  //         file.type,
  //         quality
  //       );
  //     };

  //     img.src = URL.createObjectURL(file);
  //   });
  // }, []);

  // Compress files before upload
  const compressAndUploadImages = useCallback(
    async (files, isDescription = false) => {
      if (!files || files.length === 0) return [];

      const compressedFiles = [];

      // Process files sequentially to avoid memory issues
      for (let i = 0; i < files.length; i++) {
        try {
          // const compressedFile = await compressImage(files[i], 0.6); // 60% quality
          const compressedFile = files[i]; // 60% quality
          compressedFiles.push(compressedFile);
        } catch (err) {
          console.warn(
            `Failed to compress ${
              isDescription ? "description" : "main"
            } image:`,
            err
          );
          compressedFiles.push(files[i]); // Keep original if compression fails
        }
      }

      return compressedFiles;
    },
    []
  );

  // Upload images
  const uploadAllImages = useCallback(
    async (
      mainFiles,
      descriptionFiles,
      facilityID,
      facilityName,
      facilityTypeID,
      facilityDescription
    ) => {
      if (!mainFiles?.length && !descriptionFiles?.length) {
        return true; // Nothing to upload
      }

      // Compress images before upload
      const compressedMainFiles = await compressAndUploadImages(
        mainFiles,
        false
      );
      const compressedDescFiles = await compressAndUploadImages(
        descriptionFiles,
        true
      );

      const formData = new FormData();
      formData.append("token", token);
      formData.append("Facility_ID", facilityID ?? "");

      // Append main images
      if (compressedMainFiles?.length) {
        compressedMainFiles.forEach((file) => {
          formData.append("Facility_Image", file, file.name);
        });
      }

      // Append description images
      if (compressedDescFiles?.length) {
        compressedDescFiles.forEach((file) => {
          formData.append("Facility_DesImage", file, file.name);
        });
      }

      try {
        const res = await fetch(
          `${BASEAPIURL}/UploadFacilityWeb`,
          {
            method: "POST",
            body: formData,
          }
        );
        const result = await res.json();
        if (!result.status)
          throw new Error(result.message || "Image upload failed");
        return true;
      } catch (err) {
        console.error("Image upload failed:", err);
        return false;
      }
    },
    [compressAndUploadImages]
  );

  // Fetch facilities
  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${BASEAPIURL}/GetAllFacilityListWithOutImage?Token=${encodeURIComponent(
        token
      )}`;
      // const url = `${BASEAPIURL}/GetAllFacilityList?Token=${encodeURIComponent(
      //   token
      // )}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch facilities");
      const data = await res.json();
      const allData = Array.isArray(data?.data)
        ? data.data.map((item) => ({
            ...item,
            IsBooking:
              item.IsBooking === 1 ||
              item.IsBooking === "1" ||
              item.IsBooking === true,
            IsActive:
              item.IsActive === 1 ||
              item.IsActive === "1" ||
              item.IsActive === true,
          }))
        : [];
      console.log(allData);
      setFacilities(allData);
      setTotalPages(Math.max(Math.ceil(allData.length / itemsPerPage), 1));
    } catch (err) {
      console.error("Fetch facilities failed:", err);
      setFacilities([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  // Fetch facility groups
  const fetchFacilityGroups = useCallback(async () => {
    try {
      const url = `${BASEAPIURL}/GetFGrpListS?Token=${encodeURIComponent(
        token
      )}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch facility groups");
      const data = await res.json();
      const groups = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];
      setFacilityGroups(groups);
    } catch (err) {
      console.error("Fetch groups failed:", err);
      setFacilityGroups([]);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchFacilityGroups();
    fetchFacilities();
  }, [fetchFacilityGroups, fetchFacilities]);

  // Memoize group map
  const facilityGroupMap = useMemo(() => {
    const map = {};
    facilityGroups.forEach((group) => {
      map[group.FacilityGroupID] =
        group.FacilityGroupName || group.FacilityName || "Unknown";
    });
    return map;
  }, [facilityGroups]);

  // Filter and paginate facilities
  const filteredFacilities = useMemo(() => {
    let result = facilities;

    // Apply type filter (facility group ID) if exists
    if (typeFilter) {
      result = facilities.filter(
        (facility) => facility.Facility_GrpID === typeFilter
      );
    }

    // Sort by IsActive status (active first, then inactive)
    return result.sort((a, b) => {
      if (a.IsActive === b.IsActive) return 0;
      return a.IsActive ? -1 : 1; // Active facilities come first
    });
  }, [facilities, typeFilter]);

  const paginatedFacilities = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredFacilities.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredFacilities, currentPage, itemsPerPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, facilities]);

  // Update total pages when filtered facilities change
  useEffect(() => {
    setTotalPages(
      Math.max(Math.ceil(filteredFacilities.length / itemsPerPage), 1)
    );
  }, [filteredFacilities, itemsPerPage]);

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [totalPages]
  );

  // Handle form submit
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      let facilityID = form.Facility_ID;

      const basePayload = {
        tokenData: token,
        Facility: {
          Facility_Name: form.Facility_Name,
          Facility_EC_Name: form.Facility_EC_Name,
          Facility_Description: JSON.stringify(form.Facility_Description ?? ""),
          Facility_GrpID: form.Facility_TypeID,
          // StartDate: form.StartDate || '',
          // EndDate: form.EndDate || '',
          IsActive: form.IsActive,
          IsBooking: form.IsBooking,
        },
      };

      if (!isEdit) {
        // INSERT API
        const res = await fetch(`${BASEAPIURL}/InsertFacility`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(basePayload),
        });

        const result = await res.json();
        if (!result.status) throw new Error(result.message || "Insert failed");

        // Use facility ID from response only
        if (!result.FacilityID) {
          throw new Error("Failed to retrieve Facility ID after insert.");
        }
        facilityID = result.FacilityID;
      } else {
        // UPDATE API - using PUT method
        const updatePayload = {
          tokenData: token,
          Facility: {
            ...basePayload.Facility,
          },
        };

        const res = await fetch(
          `${BASEAPIURL}/UpdateFacility?FacilityID=${form.Facility_ID}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatePayload),
          }
        );

        const result = await res.json();
        if (!result.status) throw new Error(result.message || "Update failed");
        facilityID = form.Facility_ID;
      }

      // IMAGE UPLOAD API (separate from facility update)
      const success = await uploadAllImages(
        selectedFiles,
        selectedDesFiles,
        facilityID,
        form.Facility_Name,
        form.Facility_TypeID,
        form.Facility_Description
      );

      if (!success) throw new Error("Image upload failed");

      alert(`Facility ${isEdit ? "updated" : "added"} successfully`);
      resetForm();
      setShowModal(false);
      fetchFacilities();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }, [
    form,
    isEdit,
    selectedFiles,
    selectedDesFiles,
    resetForm,
    uploadAllImages,
    fetchFacilities,
  ]);

  const handleAddClick = useCallback(() => {
    resetForm();
    setShowModal(true);
  }, [resetForm]);

  const handleEditClick = useCallback(
    (item) => {
      resetForm();
      let description = item.Facility_Description;
      try {
        description = JSON.parse(item.Facility_Description);
      } catch (e) {}

      setForm({
        Facility_ID: item.Facility_ID || "",
        Facility_Name: item.Facility_Name || "",
        Facility_EC_Name: item.Facility_EC_Name || "",
        Facility_TypeID: item.Facility_GrpID || "", // Fixed: Use Facility_GrpID instead of Facility_TypeID
        Facility_Image: Array.isArray(item.Facility_Image)
          ? item.Facility_Image
          : [],
        Facility_DesImage: Array.isArray(item.Facility_DesImage)
          ? item.Facility_DesImage
          : [],
        Facility_Description: description || "",
        // StartDate: item.StartDate || '',
        // EndDate: item.EndDate || '',
        IsActive: !!item.IsActive,
        IsBooking: !!item.IsBooking,
        Facility_EC_GrpID: item.Facility_EC_GrpID || "", // Include EC ID in edit mode
      });
      setIsEdit(true);
      setShowModal(true);
    },
    [resetForm]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    resetForm();
  }, [resetForm]);

  const handleFilterChange = (e) => {
    setTypeFilter(e.target.value);
  };

  const clearFilter = () => {
    setTypeFilter("");
  };

  const handleToggleBooking = useCallback(async (facilityId, value) => {
    const updatePayload = {
      tokenData: token,
      Facility: {
        IsBooking: form.IsBooking,
      },
    };
    try {
      const res = await fetch(
        `${BASEAPIURL}/UpdateFacility?FacilityID=${facilityId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatePayload),
        }
      );

      const result = await res.json();
      if (!result.status)
        throw new Error(result.message || "Failed to update booking status");

      // Update local state
      setFacilities((prev) =>
        prev.map((facility) =>
          facility.Facility_ID === facilityId
            ? { ...facility, IsBooking: value }
            : facility
        )
      );
    } catch (err) {
      console.error("Failed to update booking status:", err);
      alert(`Failed to update booking status: ${err.message}`);
    }
  }, []);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between mb-3">
        <h3>Facilities</h3>
        <button className="btn btn-dark" onClick={handleAddClick}>
          + Add Facility
        </button>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit" : "Add"} Facility</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Facility Name *</label>
            <input
              type="text"
              className="form-control"
              value={form.Facility_Name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, Facility_Name: e.target.value }))
              }
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">EC Facility Name</label>
            <input
              type="text"
              className="form-control"
              value={form.Facility_EC_Name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  Facility_EC_Name: e.target.value,
                }))
              }
            />
          </div>

          <div className="mb-3">
            <label className="form-label">EC Facility ID</label>
            <input
              type="text"
              className="form-control"
              value={form.Facility_EC_GrpID}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  Facility_EC_GrpID: e.target.value,
                }))
              }
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Facility Type *</label>
            <select
              className="form-control"
              value={form.Facility_TypeID}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  Facility_TypeID: e.target.value,
                }))
              }
              required
            >
              <option value="">Select Facility Type</option>
              {facilityGroups.map((group) => (
                <option
                  key={group.FacilityGroupID}
                  value={group.FacilityGroupID}
                >
                  {group.FacilityGroupName || group.FacilityName}
                </option>
              ))}
            </select>
          </div>

          {/* <div className="mb-3">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={form.StartDate}
              onChange={(e) => setForm((prev) => ({ ...prev, StartDate: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-control"
              value={form.EndDate}
              onChange={(e) => setForm((prev) => ({ ...prev, EndDate: e.target.value }))}
            />
          </div> */}

          <div className="d-flex gap-4 mb-3">
            <label className="form-check-label" htmlFor="isActiveCheck">
              Is Active
            </label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className={`form-check-input ${
                  form.IsActive ? "bg-success" : "bg-secondary"
                }`}
                id="isActiveCheck"
                checked={form.IsActive}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, IsActive: e.target.checked }))
                }
                style={{ width: "50px" }}
              />
            </div>

            <label className="form-check-label" htmlFor="isBookingCheck">
              Is Booking Enabled
            </label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className={`form-check-input ${
                  form.IsBooking ? "bg-success" : "bg-secondary"
                }`}
                id="isBookingCheck"
                checked={form.IsBooking}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, IsBooking: e.target.checked }))
                }
                style={{ width: "50px" }}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Upload Facility Images</label>
            <input
              type="file"
              ref={fileInputRef}
              className="form-control"
              multiple
              accept="image/*"
              onChange={(e) => {
                const files = Array.from(e.target.files);
                const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
                const validFiles = [];
                const tooLarge = [];

                files.forEach((file) => {
                  if (file.size <= maxSize) {
                    validFiles.push(file);
                  } else {
                    tooLarge.push(file.name);
                  }
                });

                if (tooLarge.length > 0) {
                  setErrorMsg(
                    `❌ These files exceed 5 MB: ${tooLarge.join(", ")}`
                  );
                } else {
                  setErrorMsg(""); // clear if no error
                }

                setSelectedFiles(validFiles);
              }}
            />

            {/* 🔹 Error message displayed below input */}
            {errorMsg && (
              <div className="text-danger mt-2 fw-semibold">{errorMsg}</div>
            )}

            <div className="mt-2 d-flex flex-wrap gap-2">
              {previewUrls.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt="Preview"
                  width={50}
                  height={50}
                  style={{ objectFit: "cover" }}
                />
              ))}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Upload Description Images</label>
            <input
              type="file"
              ref={desFileInputRef}
              className="form-control"
              multiple
              accept="image/*"
              onChange={(e) => {
                const files = Array.from(e.target.files);
                const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
                const validFiles = [];
                const tooLarge = [];

                files.forEach((file) => {
                  if (file.size <= maxSize) {
                    validFiles.push(file);
                  } else {
                    tooLarge.push(file.name);
                  }
                });

                if (tooLarge.length > 0) {
                  setErrorMsg(
                    `❌ These files exceed 5 MB: ${tooLarge.join(", ")}`
                  );
                } else {
                  setErrorMsg(""); // clear if no error
                }

                setSelectedFiles(validFiles);
              }}
            />

            {/* 🔹 Error message displayed below input */}
            {errorMsg && (
              <div className="text-danger mt-2 fw-semibold">{errorMsg}</div>
            )}

            <div className="mt-2 d-flex flex-wrap gap-2">
              {previewDesUrls.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt="Preview"
                  width={50}
                  height={50}
                  style={{ objectFit: "cover" }}
                />
              ))}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.Facility_Description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  Facility_Description: e.target.value,
                }))
              }
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status" />
          <p>Loading...</p>
        </div>
      ) : (
        <>
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>EC Name</th>
                {/* <th>Type ID</th> */}
                {/* <th>Start</th>
                <th>End</th> */}
                <th>Status</th>
                <th>Online Booking</th>
                <th>Images</th>
                <th>Description Images</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedFacilities.length > 0 ? (
                paginatedFacilities.map((item, idx) => {
                  const serial = (currentPage - 1) * itemsPerPage + idx + 1;
                  const typeName =
                    facilityGroupMap[item.Facility_GrpID] || "Unknown";
                  let description = item.Facility_Description;
                  try {
                    description = JSON.parse(item.Facility_Description);
                  } catch (e) {}

                  return (
                    <tr key={item.Facility_ID}>
                      <td>{serial}</td>
                      <td>{item.Facility_Name}</td>
                      <td>{item.Facility_EC_Name}</td>
                      {/* <td>{item.Facility_GrpID}</td> */}
                      {/* <td>{item.StartDate || 'N/A'}</td>
                      <td>{item.EndDate || 'N/A'}</td> */}
                      <td>
                        <span
                          className={`badge ${
                            item.IsActive ? "bg-success" : "bg-danger"
                          }`}
                        >
                          {item.IsActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input custom-switch"
                            style={{
                              width: "45px",
                              height: "20px",
                            }}
                            type="checkbox"
                            id={`switch-${item.Facility_ID}`}
                            checked={item.IsBooking}
                            onChange={(e) =>
                              handleToggleBooking(
                                item.Facility_ID,
                                e.target.checked
                              )
                            }
                          />
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {(Array.isArray(item.Facility_Image)
                            ? item.Facility_Image
                            : []
                          ).map((img, i) => (
                            <img
                              key={i}
                              src={img}
                              alt=""
                              width={50}
                              height={50}
                              style={{ objectFit: "cover" }}
                            />
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {(Array.isArray(item.Facility_DesImage)
                            ? item.Facility_DesImage
                            : []
                          ).map((img, i) => (
                            <img
                              key={i}
                              src={img}
                              alt=""
                              width={50}
                              height={50}
                              style={{ objectFit: "cover" }}
                            />
                          ))}
                        </div>
                      </td>
                      <td>{description}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEditClick(item)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" className="text-center">
                    No facilities found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
              <button
                className="btn btn-outline-dark btn-sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-outline-dark btn-sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(FacilityGroupManager);
